import {
  Button,
  Command,
  commandDefaultFilter,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  FieldTrigger,
  FieldTriggerButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from "@design-system/shadcn";
import { cn } from "@design-system/utils";
import { Check, Trash2, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { useFiltersContext } from "./data-table-filters-context";

interface DataTableFiltersFieldProps {
  /** Stable id, used for the test hook and for persisted visibility. */
  id: string;
  label: string;
  options: { label: string; value: string }[];
  /** The selected value, or `undefined` when the field is unset. */
  value?: string;
  onValueChange: (value: string | undefined) => void;
  /** Whether the reader may take the field off the row. */
  removable?: boolean;
  searchPlaceholder?: string;
  clearLabel?: string;
  removeLabel?: string;
  emptyLabel?: string;
}

/**
 * One filter field: the same Popover + `Command` + `FieldTrigger` composition
 * `MultiSelect` uses, narrowed to a single value and given the pattern's footer.
 */
function FiltersField({
  id,
  label,
  options,
  value,
  onValueChange,
  removable = true,
  searchPlaceholder = "Search...",
  clearLabel = "Clear filter",
  removeLabel = "Remove",
  emptyLabel = "No results found.",
}: DataTableFiltersFieldProps) {
  const {
    state: { visibleFieldIds },
    actions: { registerField, hideField },
  } = useFiltersContext();
  const [isOpen, setIsOpen] = useState(false);

  const isVisible = visibleFieldIds.includes(id);
  const hasValue = value !== undefined && value !== "";

  useEffect(
    () => registerField({ id, label, removable, hasValue }),
    [registerField, id, label, removable, hasValue]
  );

  // A field leaving the row takes its value with it, or the list stays narrowed
  // by a filter the reader can no longer see. Keyed on the transition, so a field
  // hidden before mount leaves a value the URL supplied alone.
  const wasVisible = useRef(isVisible);
  useEffect(() => {
    if (wasVisible.current && !isVisible) onValueChange(undefined);
    wasVisible.current = isVisible;
  }, [isVisible, onValueChange]);

  if (!isVisible) return null;

  const selected = options.find((option) => option.value === value);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <FieldTrigger
        hasValue={hasValue}
        onClear={() => onValueChange(undefined)}
        className="min-w-45"
      >
        <PopoverTrigger asChild>
          <FieldTriggerButton
            data-testid={`filter-field-${id}`}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            // The label stays in the accessible name even when a value is chosen,
            // so the reader always knows WHICH filter this is.
            aria-label={selected ? `${label}: ${selected.label}` : label}
          >
            <span
              className={cn(
                "truncate text-sm",
                !selected && "text-shape-neutral-outlined-foreground-muted"
              )}
            >
              {selected ? selected.label : label}
            </span>
          </FieldTriggerButton>
        </PopoverTrigger>
      </FieldTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-56 p-0"
        onWheel={(event) => event.stopPropagation()}
      >
        <Command
          role="listbox"
          // Item values are ids, which the default filter would score too.
          filter={(_value, search, keywords) =>
            commandDefaultFilter(keywords?.[0] ?? "", search)
          }
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  // `Command` highlights EVERY item sharing the active value.
                  value={option.value}
                  keywords={[option.label]}
                  onSelect={() => {
                    onValueChange(option.value);
                    setIsOpen(false);
                  }}
                  className="rounded-lg pr-8"
                >
                  <span className="truncate">{option.label}</span>
                  {option.value === value && (
                    <Check
                      className="text-shape-neutral-ghost-foreground-default absolute right-2 size-4"
                      aria-hidden
                    />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <Separator />
        {/* `h-8` puts the footer on the same 32px rhythm as the option rows above
            it; the DS size scale jumps 24px -> 36px with no step between. */}
        <div className="flex flex-col p-1">
          <Button
            variant="ghost"
            size="smaller"
            className="h-8 justify-start"
            onClick={() => onValueChange(undefined)}
          >
            <X aria-hidden />
            {clearLabel}
          </Button>
          {removable && (
            <Button
              variant="ghost"
              size="smaller"
              className="h-8 justify-start"
              onClick={() => {
                setIsOpen(false);
                hideField(id);
              }}
            >
              <Trash2 aria-hidden />
              {removeLabel}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { FiltersField };
export type { DataTableFiltersFieldProps };
